import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { EsgBranchMonthlyRow } from '../../types/esg-dashboard.type';
import { ESG_CONTENT_SHAPE } from '../../constants/colors';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { getChartTokens } from '../../theme/chartTheme';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

const UNASSIGNED_SITE_ID = '__unassigned__';
const MIN_COMPARE = 2;
const BRANCH_COMPARE_RADIUS = `${ESG_CONTENT_SHAPE.md}px`;

type CompareStep = 'select' | 'compare';

function safeNum(value: number | null | undefined): number {
  if (value == null || Number.isNaN(Number(value))) return 0;
  return Number(value);
}

function isUnassignedBranch(branch: EsgBranchMonthlyRow): boolean {
  return (
    branch.collection_site_id === UNASSIGNED_SITE_ID ||
    branch.collection_site_id.toLowerCase().includes('unassigned')
  );
}

function branchLabel(branch: EsgBranchMonthlyRow, t: (key: string) => string): string {
  if (isUnassignedBranch(branch)) {
    return t('dashboard.branch.compare.unassignedBranch');
  }
  return (
    branch.collection_site_name?.trim() ||
    t('dashboard.branch.compare.unnamedBranch')
  );
}

function truncateLabel(label: string, max = 16): string {
  return label.length > max ? `${label.slice(0, max)}…` : label;
}

interface CompareMetric {
  key: string;
  label: string;
  format: (row: EsgBranchMonthlyRow) => string;
  barValue?: (row: EsgBranchMonthlyRow) => number;
}

interface BranchChartRow {
  id: string;
  name: string;
  fullName: string;
  wasteDiverted: number;
  carbonAvoided: number;
  fillReports: number;
  diversionRate: number;
}

export interface BranchComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  branches: EsgBranchMonthlyRow[];
  /** Self-setup / help: tip on select step, then results step. */
  tourTip?: 'select' | 'results' | null;
  onTourNext?: () => void;
  onTourDismiss?: () => void;
  tourTitle?: string;
  tourSubtitle?: string;
  tourNextLabel?: string;
}

const BranchComparisonDialog: React.FC<BranchComparisonDialogProps> = ({
  open,
  onClose,
  branches,
  tourTip = null,
  onTourNext,
  onTourDismiss,
  tourTitle,
  tourSubtitle,
  tourNextLabel,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const tokens = useMemo(() => getChartTokens(theme), [theme]);
  const fmtNum = (n: number | null | undefined, decimals = 2) =>
    fmt.number(safeNum(n), { maximumFractionDigits: decimals });
  const fmtTco2e = (n: number | null | undefined) => fmt.metric('tco2e', safeNum(n)).text;
  const valueAxisWidth = (fmt.lang || 'en').startsWith('en') ? 48 : 76;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [step, setStep] = useState<CompareStep>('select');

  const compareMetrics: CompareMetric[] = useMemo(
    () => [
      {
        key: 'waste_diverted',
        label: t('dashboard.branch.compare.metrics.totalWaste'),
        format: (row) => `${fmtNum(row.total_waste_diverted_kg)} ${t('units.kg')}`,
        barValue: (row) => safeNum(row.total_waste_diverted_kg),
      },
      {
        key: 'carbon_avoided',
        label: t('dashboard.branch.compare.metrics.carbonAvoided'),
        format: (row) => `${fmtTco2e(row.total_avoided_tco2e)} ${t('units.tco2e')}`,
        barValue: (row) => safeNum(row.total_avoided_tco2e),
      },
      {
        key: 'fill_reports',
        label: t('dashboard.branch.compare.metrics.fillReports'),
        format: (row) => fmtNum(row.fill_level_report_count, 0),
        barValue: (row) => safeNum(row.fill_level_report_count),
      },
      {
        key: 'diversion',
        label: t('dashboard.branch.compare.metrics.diversionRate'),
        format: (row) => `${fmtNum(row.waste_diversion_rate_percent, 1)}%`,
        barValue: (row) => safeNum(row.waste_diversion_rate_percent),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, fmt],
  );

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setStep('select');
      return;
    }
    if (tourTip === 'results') {
      setStep('compare');
    } else if (tourTip === 'select') {
      setStep('select');
    }
  }, [open, tourTip]);

  const selectedBranches = useMemo(
    () => branches.filter((b) => selectedIds.has(b.collection_site_id)),
    [branches, selectedIds],
  );

  const chartData: BranchChartRow[] = useMemo(
    () =>
      selectedBranches.map((branch) => {
        const fullName = branchLabel(branch, t);
        return {
          id: branch.collection_site_id,
          name: truncateLabel(fullName),
          fullName,
          wasteDiverted: safeNum(branch.total_waste_diverted_kg),
          carbonAvoided: safeNum(branch.total_avoided_tco2e),
          fillReports: safeNum(branch.fill_level_report_count),
          diversionRate: safeNum(branch.waste_diversion_rate_percent),
        };
      }),
    [selectedBranches, t],
  );

  const toggleBranch = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === branches.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(branches.map((b) => b.collection_site_id)));
    }
  };

  const canCompare = selectedIds.size >= MIN_COMPARE || tourTip === 'results';
  const activeStep = step === 'select' ? 0 : 1;

  const maxByMetric = useMemo(() => {
    const max: Record<string, number> = {};
    for (const metric of compareMetrics) {
      if (!metric.barValue) continue;
      max[metric.key] = Math.max(
        0,
        ...selectedBranches.map((row) => metric.barValue!(row)),
      );
    }
    return max;
  }, [compareMetrics, selectedBranches]);

  const chartTooltipStyle = tokens.tooltip.contentStyle;
  const axisProps = { tick: tokens.axis.tick, axisLine: false, tickLine: false } as const;

  const chartPaperSx = {
    p: { xs: 2, sm: 2.5 },
    border: 1,
    borderColor: 'divider',
    borderRadius: BRANCH_COMPARE_RADIUS,
    bgcolor: 'background.paper',
  };

  const sectionTitleSx = {
    fontWeight: 600,
    mb: 2,
  } as const;

  const metricRowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 1.5,
  } as const;

  const renderBranchSelectCard = (branch: EsgBranchMonthlyRow) => {
    const label = branchLabel(branch, t);
    const selected = selectedIds.has(branch.collection_site_id);
    const unassigned = isUnassignedBranch(branch);

    return (
      <Grid item xs={12} sm={6} key={branch.collection_site_id}>
        <Paper
          elevation={0}
          onClick={() => toggleBranch(branch.collection_site_id)}
          sx={{
            p: 2.25,
            height: '100%',
            cursor: 'pointer',
            position: 'relative',
            borderRadius: BRANCH_COMPARE_RADIUS,
            border: '1px solid',
            borderColor: selected ? 'primary.main' : 'divider',
            boxShadow: selected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
            bgcolor: selected
              ? alpha(theme.palette.primary.main, 0.04)
              : theme.palette.background.paper,
            transition: 'border-color 160ms ease, background-color 160ms ease',
            '&:hover': {
              borderColor: selected ? 'primary.main' : 'text.secondary',
            },
          }}
        >
          {selected && (
            <CheckCircleIcon
              color="primary"
              sx={{
                position: 'absolute',
                top: 12,
                insetInlineEnd: 12,
                fontSize: 22,
              }}
            />
          )}

          <Typography
            variant="subtitle2"
            sx={{ paddingInlineEnd: 4, lineHeight: 1.35 }}
            noWrap
            title={label}
          >
            {label}
          </Typography>

          {!unassigned && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                display: 'block',
                mt: 0.5,
                opacity: 0.85,
              }}
            >
              {branch.collection_site_id}
            </Typography>
          )}

          <Box
            sx={{
              mt: 2,
              pt: 1.75,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Stack spacing={1}>
              <Box sx={metricRowSx}>
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.branch.compare.metrics.totalWaste')}
                </Typography>
                <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}>
                  <bdi>{fmtNum(branch.total_waste_diverted_kg)}</bdi> {t('units.kg')}
                </Typography>
              </Box>
              <Box sx={metricRowSx}>
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.branch.compare.metrics.carbonAvoided')}
                </Typography>
                <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}>
                  <bdi>{fmtTco2e(branch.total_avoided_tco2e)}</bdi> {t('units.tco2e')}
                </Typography>
              </Box>
              <Box sx={metricRowSx}>
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.branch.compare.metrics.diversionRate')}
                </Typography>
                <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}>
                  <bdi>{fmtNum(branch.waste_diversion_rate_percent, 1)}%</bdi>
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const renderSelectStep = () => (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {t('dashboard.branch.compare.subtitle')}
      </Typography>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {t('dashboard.branch.compare.selectBranches')}
          {selectedIds.size > 0 && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ marginInlineStart: 1 }}
            >
              ({t('dashboard.branch.compare.selectedCount', { count: selectedIds.size })})
            </Typography>
          )}
        </Typography>
        <Button
          size="small"
          onClick={handleSelectAll}
          color="inherit"
        >
          {selectedIds.size === branches.length
            ? t('dashboard.branch.compare.clearAll')
            : t('dashboard.branch.compare.selectAll')}
        </Button>
      </Stack>

      {branches.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.branch.compare.empty')}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {branches.map(renderBranchSelectCard)}
        </Grid>
      )}

      {!canCompare && selectedIds.size > 0 && (
        <Typography variant="caption" color="text.secondary">
          {t('dashboard.branch.compare.minBranches', { count: MIN_COMPARE })}
        </Typography>
      )}
    </Stack>
  );

  const renderCompareStep = () => (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {t('dashboard.branch.compare.compareSubtitle', { count: selectedBranches.length })}
      </Typography>

      <Paper elevation={0} sx={chartPaperSx}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          {t('dashboard.branch.compare.charts.wasteTitle')}
        </Typography>
        <Box dir="ltr" sx={{ minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
            <CartesianGrid stroke={tokens.grid.stroke} vertical={false} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis {...axisProps} width={valueAxisWidth} tickFormatter={(v) => fmt.compact(v)} />
            <Tooltip
              contentStyle={chartTooltipStyle}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ''}
              formatter={(value: number, name: string) => [`${fmtNum(value)} ${t('units.kg')}`, name]}
            />
            <Bar
              {...tokens.animation}
              dataKey="wasteDiverted"
              name={t('dashboard.branch.compare.metrics.totalWaste')}
              fill={tokens.seriesColor(0)}
              radius={tokens.bar.radius} maxBarSize={tokens.bar.maxBarSize}
            />
          </BarChart>
        </ResponsiveContainer>
            </Box>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ ...chartPaperSx, height: '100%' }}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              {t('dashboard.branch.compare.charts.carbonTitle')}
            </Typography>
            <Box dir="ltr" sx={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid stroke={tokens.grid.stroke} vertical={false} />
                <XAxis dataKey="name" {...axisProps} />
                <YAxis {...axisProps} width={valueAxisWidth} tickFormatter={(v) => fmt.compact(v)} />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ''}
                  formatter={(value: number) => [
                    `${fmtTco2e(value)} ${t('units.tco2e')}`,
                    t('dashboard.branch.compare.metrics.carbonAvoided'),
                  ]}
                />
                <Bar
                  {...tokens.animation}
                  dataKey="carbonAvoided"
                  name={t('dashboard.branch.compare.metrics.carbonAvoided')}
                  fill={tokens.seriesColor(4)}
                  radius={tokens.bar.radius} maxBarSize={tokens.bar.maxBarSize}
                />
              </BarChart>
            </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ ...chartPaperSx, height: '100%' }}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              {t('dashboard.branch.compare.charts.fillReportsTitle')}
            </Typography>
            <Box dir="ltr" sx={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid stroke={tokens.grid.stroke} vertical={false} />
                <XAxis dataKey="name" {...axisProps} />
                <YAxis {...axisProps} width={40} allowDecimals={false} />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ''}
                />
                <Bar
                  {...tokens.animation}
                  dataKey="fillReports"
                  name={t('dashboard.branch.compare.metrics.fillReports')}
                  fill={tokens.seriesColor(2)}
                  radius={tokens.bar.radius} maxBarSize={tokens.bar.maxBarSize}
                />
              </BarChart>
            </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ ...chartPaperSx, height: '100%' }}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              {t('dashboard.branch.compare.charts.diversionTitle')}
            </Typography>
            <Box dir="ltr" sx={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid stroke={tokens.grid.stroke} vertical={false} />
                <XAxis dataKey="name" {...axisProps} />
                <YAxis {...axisProps} width={44} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ''}
                  formatter={(value: number) => [
                    `${fmtNum(value, 1)}%`,
                    t('dashboard.branch.compare.metrics.diversionRate'),
                  ]}
                />
                <Bar
                  {...tokens.animation}
                  dataKey="diversionRate"
                  name={t('dashboard.branch.compare.metrics.diversionRate')}
                  fill={tokens.seriesColor(1)}
                  radius={tokens.bar.radius} maxBarSize={tokens.bar.maxBarSize}
                />
              </BarChart>
            </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          {t('dashboard.branch.compare.resultsTitle')}
        </Typography>
        <TableContainer
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: BRANCH_COMPARE_RADIUS,
            overflow: 'auto',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 160 }}>
                  {t('dashboard.branch.compare.metric')}
                </TableCell>
                {selectedBranches.map((branch, index) => (
                  <TableCell
                    key={branch.collection_site_id}
                    align="right"
                    sx={{ minWidth: 120 }}
                  >
                    <Stack alignItems="flex-end" spacing={0.5}>
                      <Typography variant="caption" fontWeight={600} noWrap title={branchLabel(branch, t)}>
                        {branchLabel(branch, t)}
                      </Typography>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '2px',
                          bgcolor: tokens.seriesColor(index),
                        }}
                      />
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {compareMetrics.map((metric) => (
                <TableRow
                  key={metric.key}
                  sx={{
                    '&:last-child td, &:last-child th': { borderBottom: 0 },

                  }}
                >
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    {metric.label}
                  </TableCell>
                  {selectedBranches.map((branch) => {
                    const barMax = maxByMetric[metric.key] ?? 0;
                    const barVal = metric.barValue?.(branch) ?? 0;
                    const pct = barMax > 0 ? (barVal / barMax) * 100 : 0;
                    return (
                      <TableCell key={branch.collection_site_id} align="right">
                        <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          <bdi>{metric.format(branch)}</bdi>
                        </Typography>
                        {metric.barValue && barMax > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={step === 'compare' ? 'lg' : 'md'}
      fullWidth
      hideBackdrop={Boolean(tourTip)}
      PaperProps={{ sx: { overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,

        }}
      >
        {t('dashboard.branch.compare.title')}
      </DialogTitle>

      <Box sx={{ px: 3, pb: 2 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            '& .MuiStepLabel-label': {
              typography: 'caption',
              fontWeight: 500,
            },
          }}
        >
          <Step>
            <StepLabel>{t('dashboard.branch.compare.stepSelect')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('dashboard.branch.compare.stepCompare')}</StepLabel>
          </Step>
        </Stepper>
      </Box>

      <SetupCoachmark
        open={tourTip === 'select' || tourTip === 'results'}
        title={tourTitle ?? t('selfSetup.tour.dashboardCompareDialogTitle')}
        subtitle={tourSubtitle ?? t('selfSetup.tour.dashboardCompareDialogSubtitle')}
        onDismiss={() => onTourDismiss?.()}
        onNext={() => onTourNext?.()}
        nextLabel={tourNextLabel ?? t('selfSetup.tourNext')}
        fullWidth
        placement={tourTip === 'select' ? 'left' : 'top'}
      >
        <DialogContent
          dividers
          id={
            tourTip === 'results'
              ? 'self-setup-dashboard-compare-results'
              : 'self-setup-dashboard-compare-dialog'
          }
          {...(tourTip ? { 'data-setup-hole': true } : {})}
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            bgcolor: 'background.default',
          }}
        >
          {step === 'select' ? renderSelectStep() : renderCompareStep()}
        </DialogContent>
      </SetupCoachmark>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
        }}
      >
        {step === 'compare' ? (
          <>
            <Button onClick={() => setStep('select')} color="inherit">
              {t('common.back')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose} color="inherit">
              {t('common.close')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} color="inherit">
              {t('common.close')}
            </Button>
            <Button
              variant="contained"
              disabled={!canCompare}
              onClick={() => setStep('compare')}
              sx={{ px: 2.5 }}
            >
              {t('dashboard.branch.compare.next')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BranchComparisonDialog;
